import type { RingSegment } from "../../components/screen0-ring/Screen0Ring";
import type { TodayEntity } from "./todayAggregation";

export type PeriodMode = "day" | "week" | "month";

export type AveragablePeriodData = {
  totalSeconds: number;
  entities: TodayEntity[];
  ringEntities: TodayEntity[];
  segments: RingSegment[];
  entitiesTotalSeconds: number;
  entitiesTotalOpens: number;
  unlockCount: number | undefined;
};

type WeekMonthSnapshot = { daysWithData: number };

type WeekOverview = { week: WeekMonthSnapshot };
type MonthOverview = { month: WeekMonthSnapshot };

/** Divides a scalar by daysWithData; returns the original value when divisor is invalid. */
export function dailyAverageValue(value: number, daysWithData: number): number {
  if (daysWithData <= 0) return value;
  return value / daysWithData;
}

export function resolveDaysWithData(
  mode: PeriodMode,
  overview: WeekOverview | MonthOverview | Record<string, unknown>,
): number {
  if (mode === "week") return (overview as WeekOverview).week?.daysWithData ?? 0;
  if (mode === "month") return (overview as MonthOverview).month?.daysWithData ?? 0;
  return 0;
}

export function shouldApplyDailyAverage(
  mode: PeriodMode,
  showDailyAverage: boolean,
  daysWithData: number,
): boolean {
  return showDailyAverage && (mode === "week" || mode === "month") && daysWithData > 0;
}

function averageEntity(entity: TodayEntity, daysWithData: number): TodayEntity {
  return {
    ...entity,
    durationSeconds: dailyAverageValue(entity.durationSeconds, daysWithData),
    opens: entity.opens !== undefined ? dailyAverageValue(entity.opens, daysWithData) : undefined,
  };
}

function averageSegment(segment: RingSegment, daysWithData: number): RingSegment {
  return {
    ...segment,
    seconds: dailyAverageValue(segment.seconds, daysWithData),
    opens: segment.opens !== undefined ? dailyAverageValue(segment.opens, daysWithData) : undefined,
  };
}

/** Scales all summed period totals by 1 / daysWithData so ring proportions stay consistent. */
export function applyDailyAverageToPeriodData<T extends AveragablePeriodData>(
  data: T,
  daysWithData: number,
): T {
  if (daysWithData <= 0) return data;

  return {
    ...data,
    totalSeconds: dailyAverageValue(data.totalSeconds, daysWithData),
    entities: data.entities.map((entity) => averageEntity(entity, daysWithData)),
    ringEntities: data.ringEntities.map((entity) => averageEntity(entity, daysWithData)),
    segments: data.segments.map((segment) => averageSegment(segment, daysWithData)),
    entitiesTotalSeconds: dailyAverageValue(data.entitiesTotalSeconds, daysWithData),
    entitiesTotalOpens: dailyAverageValue(data.entitiesTotalOpens, daysWithData),
    unlockCount:
      data.unlockCount !== undefined
        ? dailyAverageValue(data.unlockCount, daysWithData)
        : undefined,
  };
}
